import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import type { Root } from 'react-dom/client';
import type { PrintJob, RenderToPdfPayload, RendererPrintToPDFOptions } from '../types';
import { ElectronPrintingApi } from './ElectronPrintingApi';
import { ServerStyleSheet } from 'styled-components';
// Inline critical CSS so Windows data: URL rendering does not miss styles
// Vite's ?raw loads file content as string at build time
// Tailwind utilities and print rules are both needed for correct layout
import tailwindCss from 'assets/css/tailwind.css?raw';
import printCss from 'assets/css/print.css?raw';

export interface RenderJobOverrides {
  fileName?: string;
  directory?: string;
  timeoutMs?: number;
  pdfOptions?: RendererPrintToPDFOptions;
}

export type RenderJobOverridesResolver =
  | RenderJobOverrides
  | ((job: PrintJob) => RenderJobOverrides | undefined);

export interface RenderedPdfJob {
  jobId: string;
  path: string;
}

const PRINT_ROOT_ATTRIBUTE = 'data-print-job';

export class RendererPdfService {
  constructor(
    private readonly electronApi = ElectronPrintingApi,
    private readonly renderToString: typeof renderToStaticMarkup = renderToStaticMarkup,
  ) {}

  private pendingStyleTags: string[] = [];

  buildHtmlForJob(job: PrintJob): string {
    if (typeof document === 'undefined') {
      throw new Error('RendererPdfService requires a browser environment');
    }

    const doc = document;
    const htmlAttributes = this.buildHtmlAttributes(doc);
    const headMarkup = this.collectHeadMarkup(doc);
    const baseHref = this.escapeAttribute(this.resolveBaseHref(doc));
    const jobContent = this.renderJobContent(job);
    const scopedStyles = this.consumePendingStyleTags();
    const inlineCss = this.buildInlineCss();
    const jobIdAttr = this.escapeAttribute(job.id);
    const bodyAttributes = this.buildBodyAttributes(doc);

    const htmlParts = [
      '<!DOCTYPE html>',
      `<html${htmlAttributes}>`,
      '<head>',
      '<meta charset="UTF-8"/>',
      `<base href="${baseHref}" />`,
      headMarkup,
      inlineCss,
      scopedStyles,
      '</head>',
      `<body${bodyAttributes}>`,
      `<div ${PRINT_ROOT_ATTRIBUTE}="${jobIdAttr}">${jobContent}</div>`,
      '</body>',
      '</html>',
    ];

    return htmlParts.join('');
  }

  async renderJobToPdf(job: PrintJob, overrides?: RenderJobOverrides): Promise<RenderedPdfJob> {
    const html = this.buildHtmlForJob(job);
    const payload = this.buildPayloadForJob(job, html, overrides);

    try {
      const response = await this.electronApi.renderToPDF(payload);
      return {
        jobId: job.id,
        path: response.path,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to render PDF for job ${job.id}: ${error.message}`, { cause: error });
      }
      throw new Error(`Failed to render PDF for job ${job.id}`);
    }
  }

  async renderJobsToPdfs(
    jobs: PrintJob[],
    overrides?: RenderJobOverridesResolver,
  ): Promise<Record<string, string>> {
    const results = await Promise.all(
      jobs.map(async (job) => {
        const jobOverrides = typeof overrides === 'function' ? overrides(job) : overrides;
        const rendered = await this.renderJobToPdf(job, jobOverrides);
        return [rendered.jobId, rendered.path] as const;
      }),
    );

    return results.reduce<Record<string, string>>((accumulator, [jobId, path]) => {
      accumulator[jobId] = path;
      return accumulator;
    }, {});
  }

  private renderJobContent(job: PrintJob): string {
    const element = <>{job.content}</>;

    try {
      return this.renderWithServerStyles(element);
    } catch (error) {
      if (typeof document === 'undefined') {
        throw error;
      }
      return this.renderWithDomFallback(element);
    }
  }

  private renderWithServerStyles(element: React.ReactElement): string {
    const sheet = new ServerStyleSheet();
    try {
      const html = this.renderToString(sheet.collectStyles(element));
      const styleTags = sheet.getStyleTags();
      if (styleTags) {
        this.pendingStyleTags.push(styleTags);
      }
      return html;
    } finally {
      sheet.seal();
    }
  }

  private renderWithDomFallback(element: React.ReactElement): string {
    const container = document.createElement('div');
    container.setAttribute('data-renderer-pdf-fallback', 'true');
    container.style.position = 'fixed';
    container.style.pointerEvents = 'none';
    container.style.opacity = '0';
    container.style.width = '0';
    container.style.height = '0';
    container.style.overflow = 'hidden';
    container.style.top = '-10000px';
    container.style.left = '-10000px';

    const host = document.body ?? document.documentElement;
    if (!host) {
      throw new Error('Cannot render fallback without a document host element');
    }
    host.appendChild(container);

    let root: Root | null = null;

    try {
      root = createRoot(container);
      flushSync(() => {
        root?.render(element);
      });
      const html = container.innerHTML;
      flushSync(() => {
        root?.unmount();
      });
      return html;
    } finally {
      container.remove();
    }
  }

  private collectHeadMarkup(doc: Document): string {
    const nodes = Array.from(
      doc.head?.querySelectorAll('link[rel~="stylesheet"], link[rel="stylesheet"], style') ?? [],
    );
    return nodes.map((node) => node.outerHTML ?? '').join('');
  }

  private buildInlineCss(): string {
    try {
      const chunks: string[] = [];
      if (typeof tailwindCss === 'string' && tailwindCss.trim().length) {
        chunks.push(tailwindCss);
      }
      if (typeof printCss === 'string' && printCss.trim().length) {
        chunks.push(printCss);
      }
      if (!chunks.length) return '';
      return `<style>${chunks.join('\n')}</style>`;
    } catch {
      return '';
    }
  }

  private consumePendingStyleTags(): string {
    if (!this.pendingStyleTags.length) {
      return '';
    }
    const markup = this.pendingStyleTags.join('');
    this.pendingStyleTags = [];
    return markup;
  }

  private buildBodyAttributes(doc: Document): string {
    const attributes: string[] = [];
    const classList = new Set<string>();
    const existingClasses = doc.body?.getAttribute('class');

    if (existingClasses) {
      existingClasses
        .split(/\s+/)
        .map((value) => value.trim())
        .filter(Boolean)
        .forEach((value) => classList.add(value));
    }

    classList.add('toprint');

    if (classList.size > 0) {
      attributes.push(` class="${this.escapeAttribute(Array.from(classList).join(' '))}"`);
    }

    const dir = doc.body?.getAttribute('dir');
    if (dir) {
      attributes.push(` dir="${this.escapeAttribute(dir)}"`);
    }

    return attributes.join('');
  }

  private resolveBaseHref(doc: Document): string {
    if (doc.baseURI) {
      return doc.baseURI;
    }

    if (typeof window !== 'undefined' && window.location) {
      return window.location.href;
    }

    return 'about:blank';
  }

  private buildHtmlAttributes(doc: Document): string {
    const lang = doc.documentElement?.getAttribute('lang');
    const dir = doc.documentElement?.getAttribute('dir');
    const attributes: string[] = [];

    if (lang) {
      attributes.push(` lang="${this.escapeAttribute(lang)}"`);
    }
    if (dir) {
      attributes.push(` dir="${this.escapeAttribute(dir)}"`);
    }

    return attributes.join('');
  }

  private escapeAttribute(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private buildPayloadForJob(
    job: PrintJob,
    html: string,
    overrides?: RenderJobOverrides,
  ): RenderToPdfPayload {
    const payload: RenderToPdfPayload = {
      html,
      fileName: this.ensurePdfExtension(this.deriveFileName(job, overrides?.fileName)),
      pdfOptions: this.buildPdfOptions(job, overrides?.pdfOptions),
    };

    if (overrides?.directory) {
      payload.directory = overrides.directory;
    }
    if (typeof overrides?.timeoutMs === 'number') {
      payload.timeoutMs = overrides.timeoutMs;
    }

    return payload;
  }

  private buildPdfOptions(
    job: PrintJob,
    custom?: RendererPrintToPDFOptions,
  ): RendererPrintToPDFOptions {
    const defaults: RendererPrintToPDFOptions = {
      printBackground: true,
      pageSize: job.metadata?.format === 'A3' ? 'A3' : 'A4',
      preferCSSPageSize: true,
    };

    const orientation = job.metadata?.orientation;
    if (orientation) {
      defaults.landscape = orientation === 'landscape';
    }

    const marginSetting = job.metadata?.margin ?? 'none';
    if (marginSetting === 'none') {
      defaults.marginsType = 1;
      defaults.margins = { top: 0, bottom: 0, left: 0, right: 0 };
    } else if (marginSetting === 'default') {
      defaults.marginsType = 0;
    }

    return custom ? { ...defaults, ...custom } : defaults;
  }

  private ensurePdfExtension(fileName: string): string {
    const trimmed = fileName.trim();
    if (trimmed.toLowerCase().endsWith('.pdf')) {
      return trimmed;
    }
    return `${trimmed}.pdf`;
  }

  private deriveFileName(job: PrintJob, override?: string): string {
    if (override && override.trim()) {
      return override.trim();
    }

    const typeSegment = this.sanitiseSegment(job.type);
    const idSegment = this.sanitiseSegment(job.id);
    return `${typeSegment}-${idSegment}`;
  }

  private sanitiseSegment(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'print';
  }
}

export default RendererPdfService;
